import {
  FUNNEL_FREQUENCY_X_AXIS_KEY,
  FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY,
  FunnelPostfixOnColumnName,
  FunnelXAxisKey,
  IHighlightColumn,
  ILineMetaNewFunnel,
  IParsedChartDataFunnel,
  IParsedChartDataRecord,
  IParsedFunnelColumn,
  IParsedFunnelModeSlice,
  IRange,
} from '../../components/Chart/interfaces';
import { DATA_KEY } from '../../components/Chart/constants/utils';
import {
  FunnelModeKey,
  IFunnelColumnFieldsFragment,
  IFunnelDistributionBucket,
  IFunnelModeSlice,
  IFunnelPercentFieldsFragment,
  IMeasureControl,
  QueryType,
  TimeUnit,
} from '../apollo/graphqlSchema';
import { isNil, max, min } from 'lodash';
import {
  findByKey,
  generateHighlights,
  getAllSlices,
  parseAliasReplacements,
  toAliasableSlice,
  toDefaultHasAlias,
} from './util';
import { assertCondition, assertNotNil } from '../types';
import { MaybeNil } from '../interfaces';
import { timeUnitValueToSeconds } from '../timeUnits';
import { IFunnelAnalysis } from '../apollo/responseTypes';

/**
 * Returns a map by measureKey (stepKey) and corresponding percent value
 */
const getPercentsByMeasure = (
  percents: IFunnelPercentFieldsFragment[],
  columns: IFunnelColumnFieldsFragment[],
) => {
  return percents.reduce<Record<string, IFunnelPercentFieldsFragment>>(
    (acc, percent) => {
      const measureKey = columns.find((c) => c.key === percent.key)?.measureKey;
      if (!isNil(measureKey)) {
        assertCondition(
          !acc.hasOwnProperty(measureKey),
          'duplicate percents detected',
        );
        acc[measureKey] = percent;
      }
      return acc;
    },
    {},
  );
};

export const getPostfixDataKey = (
  columnKey: string,
  postfix: FunnelPostfixOnColumnName,
  measureKey: string,
) => columnKey + postfix + ':' + measureKey;

const setPercentValues = (
  dataForDay: IParsedChartDataRecord,
  columnKey: string,
  measureKey: string,
  percentValue: MaybeNil<number>,
  bounceValue: MaybeNil<number>,
  bouncePercent: MaybeNil<number>,
) => {
  const percentKey = getPostfixDataKey(
    columnKey,
    FunnelPostfixOnColumnName.percentKey,
    measureKey,
  );
  dataForDay[percentKey] = percentValue ?? 0;
  const bounceKey = getPostfixDataKey(
    columnKey,
    FunnelPostfixOnColumnName.bounceKey,
    measureKey,
  );
  dataForDay[bounceKey] = bounceValue ?? 0;
  const bouncePercentKey = getPostfixDataKey(
    columnKey,
    FunnelPostfixOnColumnName.bouncePercentKey,
    measureKey,
  );
  dataForDay[bouncePercentKey] = bouncePercent ?? 0;
};

/**
 * Create data structure suitable for feeding Recharts: each element corresponds to a distinct X-Axis tick.
 */
const generateChartDataFromColumnsConversion = (
  funnel: IFunnelAnalysis,
): IParsedChartDataRecord[] => {
  const chartData: IParsedChartDataRecord[] = [];
  assertNotNil(funnel.data);
  const {
    data: { columns, dates, live, isEmpty },
  } = funnel;

  if (isEmpty) {
    return [];
  }

  for (let i = 0; i < dates.length; i++) {
    const dataForDay: IParsedChartDataRecord = { [DATA_KEY]: dates[i] };
    for (const column of columns) {
      const { key, measureKey, percents, values } = column;
      // null values from Kobol are coerced to 0 (KB-4949) to mimic pre-overall behavior (KB-3960)
      // The coercing makes the following difference:
      // * there is connecting line between null (now displayed as 0) and non-null values in case of a line chart
      // * there is a "0 (0%)" tooltip on these null values
      dataForDay[key] = values[i] ?? 0;

      setPercentValues(
        dataForDay,
        key,
        measureKey,
        dataForDay[key] === 0 ? 0 : 1,
        0,
        0,
      );

      // if current column is not result measure, add percent value in data.
      const percentsByMeasure = getPercentsByMeasure(percents, columns);
      for (const [percentMeasureKey, funnelPercents] of Object.entries(
        percentsByMeasure,
      )) {
        setPercentValues(
          dataForDay,
          key,
          percentMeasureKey,
          funnelPercents.values[i],
          funnelPercents.bounceCounts[i],
          funnelPercents.bouncePercents[i],
        );
      }
    }
    dataForDay.isLive = live[i];
    chartData.push(dataForDay);
  }
  return chartData;
};

const generateFrequencyBucketLabel = (
  bucket: IFunnelDistributionBucket,
): string => {
  if (bucket.endExclusive - bucket.startInclusive <= 1) {
    const unit = bucket.startInclusive === 1 ? 'time' : 'times';
    return `${bucket.startInclusive} ${unit}`;
  } else {
    return `${bucket.startInclusive}-${bucket.endExclusive} times`;
  }
};

const generateChartDataFromColumnsFrequency = (
  funnel: IFunnelAnalysis,
): IParsedChartDataRecord[] => {
  const chartData: IParsedChartDataRecord[] = [];
  assertNotNil(funnel.data);
  const {
    data: { columns, funnelXAxis: xAxis, live, isEmpty },
  } = funnel;
  if (isEmpty) {
    return chartData;
  }

  // const firstStep = measureControls.length > 0 ? measureControls[0] : undefined;
  assertCondition(xAxis?.__typename === 'FunnelFrequencyXAxis');

  for (let i = 0; i < xAxis.buckets.length; i++) {
    const bucketLabel = generateFrequencyBucketLabel(xAxis.buckets[i]);
    const dataForDay: IParsedChartDataRecord = {
      [FUNNEL_FREQUENCY_X_AXIS_KEY]: bucketLabel,
    };
    for (const column of columns) {
      const { key, percents, values } = column;
      dataForDay[key] = values[i] ?? 0;
      const percentKey = key + FunnelPostfixOnColumnName.percentKey;
      assertCondition(percents.length === 1);
      assertCondition(percents[0].key === column.key);
      dataForDay[percentKey] = percents[0].values[i];
    }
    dataForDay.isLive = live[0];
    chartData.push(dataForDay);
  }
  return chartData;
};

const generateTimeToConvertBucketLabel = (
  bucket: IFunnelDistributionBucket,
  timeUnit: TimeUnit,
) => {
  const unit = timeUnit[0].toLowerCase();
  if (bucket.endExclusive - bucket.startInclusive <= 1) {
    return `${bucket.endExclusive}${unit}`;
  } else {
    return `${bucket.startInclusive}-${bucket.endExclusive}${unit}`;
  }
};

const generateChartDataFromColumnsBuckets = (
  funnel: IFunnelAnalysis,
): [IParsedChartDataRecord[], IRange | undefined] => {
  assertNotNil(funnel.data);
  const {
    data: { columns, funnelXAxis: xAxis, live, isEmpty },
  } = funnel;

  if (isEmpty) {
    return [[], undefined];
  }

  assertCondition(xAxis?.__typename === 'FunnelTimeToConvertXAxis');

  if (!xAxis.buckets.length) {
    return [[], undefined];
  }

  const { buckets, timeUnit } = xAxis;

  const data = buckets.map((bucket, index) => {
    const dataForDay: IParsedChartDataRecord = {
      [FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY]: generateTimeToConvertBucketLabel(
        bucket,
        timeUnit,
      ),
    };

    for (const { key, percents, values } of columns) {
      assertCondition(percents.length === 1);
      assertCondition(percents[0].key === key);

      const percentKey = key + FunnelPostfixOnColumnName.percentKey;

      dataForDay[key] = values[index] ?? 0;
      dataForDay[percentKey] = percents[0].values[index];
    }

    dataForDay.isLive = live[0];

    return dataForDay;
  });
  const start = min(buckets.map((b) => b.startInclusive));
  const end = max(buckets.map((b) => b.endExclusive));
  const range: IRange | undefined =
    !isNil(start) && !isNil(end)
      ? [
          timeUnitValueToSeconds(start, timeUnit),
          timeUnitValueToSeconds(end, timeUnit),
        ]
      : undefined;
  return [data, range];
};

/**
 * Helper function to enhance each Funnel column (data series) with measure and slice name
 */
const generateColumn = (
  column: IFunnelColumnFieldsFragment,
): IParsedFunnelColumn => ({
  ...column,
  sliceName: '',
  measureName: '',
});

/**
 * Create additional (meta) information for each data series produced by {@link generateChartDataFromColumnsConversion}
 *
 * It includes information about measures (steps), slices (breakdowns) and references to other linked data series
 * (e.g. a reference from the absolule value data series to the corresponding percent data series)
 *
 * The function is exported because it has to be regenerated if measure or slice names change (e.g. when
 * adding or removing aliases)
 */
export const generateLinesMetaFromColumnsFunnel = (
  parsedColumns: IParsedFunnelColumn[],
  funnelModeSlices: IParsedFunnelModeSlice[],
  measureControls: IMeasureControl[],
  ignoreSliceNameOnDisplay: boolean,
): ILineMetaNewFunnel => {
  const linesMeta: ILineMetaNewFunnel = {};
  for (const column of parsedColumns) {
    const { key, name, measureKey, measureName, sliceName } = column;
    const funnelModeSliceKey = column.funnelModeSliceKey;
    const funnelModeSlice = !isNil(funnelModeSliceKey)
      ? findByKey(funnelModeSlices, funnelModeSliceKey)
      : undefined;

    const stepIndex = measureControls.findIndex(
      (measureControl) => measureControl.key === measureKey,
    );

    let nameWithPrefix = name;
    if (stepIndex >= 0) {
      if (ignoreSliceNameOnDisplay) {
        nameWithPrefix = `${stepIndex + 1}. ${nameWithPrefix}`;
      } else {
        nameWithPrefix = `${sliceName} - ${stepIndex + 1}: ${measureName}`;
      }
    }
    linesMeta[key] = {
      name: key,
      display: nameWithPrefix,
      measureKey,
      measureName,
      measureLabel: String(stepIndex + 1),
      sliceKey: column.sliceKey,
      sliceName: column.sliceName,
      funnelModeSlice,
    };
  }
  return linesMeta;
};

/**
 * Extract highlights that are based on comparing a column associated with the
 * first step. We display them on the Line view of the funnel, where each
 * series is compared to a column in the first step. We can't visualize any
 * highlights between current step and non-first step
 */
const filterHighlights = (
  measureControls: IMeasureControl[],
  columns: IFunnelColumnFieldsFragment[],
): IHighlightColumn[] => {
  if (measureControls.length === 0) {
    return [];
  }
  const firstStepColumnKeys = new Set(
    columns
      .filter((c) => c.measureKey === measureControls[0].key)
      .map((c) => c.key),
  );

  return columns.flatMap((column) =>
    column.percents
      // only percents compare to the first step
      .filter((columnPercents) => firstStepColumnKeys.has(columnPercents.key))
      .map<IHighlightColumn>((columnPercents) => ({
        key: column.key,
        reading: columnPercents.reading,
      })),
  );
};

const toParsedFunnelModeSlice = (
  fms: IFunnelModeSlice,
  measureControls: IMeasureControl[],
): IParsedFunnelModeSlice => {
  const firstMeasureIndex = fms.measureSelection.indexOf(true);
  assertCondition(firstMeasureIndex >= 0);
  const lastMeasureIndex = fms.measureSelection.lastIndexOf(true);
  assertCondition(lastMeasureIndex >= 0);
  const firstMeasureKey = measureControls[firstMeasureIndex].key;
  assertNotNil(firstMeasureKey);
  const lastMeasureKey = measureControls[lastMeasureIndex].key;
  assertNotNil(lastMeasureKey);
  return {
    ...fms,
    firstMeasureKey,
    lastMeasureKey,
  };
};

/**
 * Main entry point of the Funnel transformations after Funnel analysis is load
 */
export default function transformFunnelData(
  analysis: IFunnelAnalysis,
): IParsedChartDataFunnel {
  /** new data transform function */
  const mode = analysis.query?.mode?.key ?? FunnelModeKey.Conversion;
  let xAxisKey: FunnelXAxisKey | undefined;
  let data: IParsedChartDataRecord[] | undefined;
  let dates: string[] | undefined;
  let xAxisRange: IRange | undefined;
  switch (mode) {
    case FunnelModeKey.Conversion:
      data = generateChartDataFromColumnsConversion(analysis);
      xAxisKey = DATA_KEY;
      dates = data.map((dataPoint) => dataPoint[DATA_KEY]?.toString() ?? '');
      break;
    case FunnelModeKey.TimeToConvert:
      xAxisKey = FUNNEL_TIME_TO_CONVERT_X_AXIS_KEY;
      [data, xAxisRange] = generateChartDataFromColumnsBuckets(analysis);
      dates = [];
      break;
    case FunnelModeKey.Frequency:
      xAxisKey = FUNNEL_FREQUENCY_X_AXIS_KEY;
      dates = [];
      data = generateChartDataFromColumnsFrequency(analysis);
      break;
  }

  assertNotNil(analysis.data);
  const columns = analysis.data.columns.map(generateColumn);
  const slices = getAllSlices(analysis).map(toAliasableSlice);
  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements,
  );
  const measureControls = analysis.chartControl.measureControls;
  const funnelModeSlices = analysis.query.modeSlices.map((fms) =>
    toParsedFunnelModeSlice(fms, measureControls),
  );

  const linesMeta = generateLinesMetaFromColumnsFunnel(
    columns,
    funnelModeSlices,
    measureControls,
    analysis.groupbys.length === 0,
  );
  const highlights = generateHighlights(
    filterHighlights(measureControls, columns),
  );
  const measureControlsWithAlias = measureControls.map(toDefaultHasAlias);
  const xAxis = analysis.data.funnelXAxis;
  return {
    queryType: QueryType.Funnel,
    xAxisKey,
    dates,
    data,
    slices,
    columns,
    linesMeta,
    measureControls: measureControlsWithAlias,
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    highlights,
    xAxis,
    funnelModeSlices,
    mode,
    xAxisRange,
    // can be overriden if this is a transient funnel from Top Paths
    showInReportState: { isDisabled: false },
  };
}
